
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusIcon, PencilIcon, TrashIcon, CalendarIcon, Loader2, AlertCircle, AlertTriangle, Info, Search, Filter, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { LoadingState } from '@/components/ui/loading-state';
import { EmptyState } from '@/components/ui/empty-state';

type TaskPriority = "normal" | "importante" | "urgente";
type TaskFilter = "all" | "pending" | "completed";

interface Task {
  id: string;
  userId: string;
  title: string;
  descripcion?: string;
  completed: boolean;
  dueDate?: string;
  createdAt?: string;
  priority?: TaskPriority;
}

const priorityOptions: { value: TaskPriority; label: string; icon: typeof Info }[] = [
  { value: "normal", label: "Normal", icon: Info },
  { value: "importante", label: "Importante", icon: AlertCircle },
  { value: "urgente", label: "Urgente", icon: AlertTriangle },
];

const statusFilterOptions: { value: TaskFilter; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "pending", label: "Pendientes" },
  { value: "completed", label: "Completadas" },
];

const getPriorityBadgeVariant = (priority?: TaskPriority) => {
  switch (priority) {
    case 'urgente': return 'destructive';
    case 'importante': return 'warning';
    default: return 'secondary';
  }
};

const getPriorityIcon = (priority?: TaskPriority) => {
  const option = priorityOptions.find(o => o.value === (priority || 'normal'));
  return option?.icon || Info;
};

export default function Tareas() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentTask, setCurrentTask] = useState<Partial<Task> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const { fetchWithAuth } = useUser();

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TaskFilter>('all');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth('/api/tareas');
      if (!response.ok) throw new Error('Error al cargar tareas.');
      const data = await response.json();
      setTasks(data);
    } catch (error) {
      toast.error('No se pudieron cargar las tareas.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Filtered and sorted tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter
      const matchesSearch = debouncedSearch === '' ||
        task.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (task.descripcion?.toLowerCase().includes(debouncedSearch.toLowerCase()));

      // Priority filter
      const matchesPriority = priorityFilter === 'all' ||
        (task.priority || 'normal') === priorityFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'pending' && !task.completed) ||
        (statusFilter === 'completed' && task.completed);

      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [tasks, debouncedSearch, priorityFilter, statusFilter]);

  const pendingTasks = useMemo(() => filteredTasks.filter(t => !t.completed), [filteredTasks]);
  const completedTasks = useMemo(() => filteredTasks.filter(t => t.completed), [filteredTasks]);

  const hasActiveFilters = searchTerm !== '' || priorityFilter !== 'all' || statusFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setPriorityFilter('all');
    setStatusFilter('all');
  };

  const handleNew = () => {
    setCurrentTask({ title: '', descripcion: '', completed: false, priority: 'normal' });
    setSelectedDate(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (task: Task) => {
    setCurrentTask(task);
    setSelectedDate(task.dueDate ? new Date(task.dueDate) : undefined);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!currentTask || !currentTask.title?.trim()) {
      toast.error('El título es obligatorio.');
      return;
    }

    const apiPath = currentTask.id ? `/api/tareas/${currentTask.id}` : '/api/tareas';
    const method = currentTask.id ? 'PUT' : 'POST';

    const taskData = {
      title: currentTask.title,
      descripcion: currentTask.descripcion || '',
      completed: currentTask.completed || false,
      dueDate: selectedDate ? selectedDate.toISOString() : null,
      priority: currentTask.priority || 'normal',
    };

    const promise = fetchWithAuth(apiPath, {
      method,
      body: JSON.stringify(taskData),
    });

    toast.promise(promise, {
      loading: 'Guardando tarea...',
      success: async () => {
        await fetchTasks();
        setDialogOpen(false);
        setCurrentTask(null);
        setSelectedDate(undefined);
        return `Tarea ${currentTask.id ? 'actualizada' : 'creada'} con éxito.`;
      },
      error: (err) => `No se pudo guardar la tarea: ${err.message}`,
    });
  };

  const handleToggleComplete = async (task: Task) => {
    const promise = fetchWithAuth(`/api/tareas/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify({ completed: !task.completed }),
    });

    toast.promise(promise, {
      loading: 'Actualizando...',
      success: () => {
        fetchTasks();
        return 'Tarea actualizada.';
      },
      error: 'No se pudo actualizar la tarea.',
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    const promise = fetchWithAuth(`/api/tareas/${deleteId}`, { method: 'DELETE' });
    toast.promise(promise, {
      loading: 'Eliminando tarea...',
      success: () => {
        fetchTasks();
        setDeleteId(null);
        return 'Tarea eliminada.';
      },
      error: 'No se pudo eliminar la tarea.',
    });
  };

  const isOverdue = (task: Task) => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date() && !task.completed;
  };

  const TaskRow = ({ task }: { task: Task }) => {
    const PriorityIcon = getPriorityIcon(task.priority);
    return (
      <TableRow key={task.id} className={cn(task.completed && "opacity-60")}>
        <TableCell className="w-12">
          <Checkbox
            checked={task.completed}
            onCheckedChange={() => handleToggleComplete(task)}
          />
        </TableCell>
        <TableCell className={cn("font-medium", task.completed && "line-through")}>
          <div className="flex flex-col gap-1">
            <span>{task.title}</span>
            {task.descripcion && (
              <span className="text-xs text-muted-foreground line-clamp-1">{task.descripcion}</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={getPriorityBadgeVariant(task.priority)} className="gap-1 capitalize">
            <PriorityIcon className="h-3 w-3" />
            {task.priority || 'normal'}
          </Badge>
        </TableCell>
        <TableCell>
          {task.dueDate && (
            <Badge variant={isOverdue(task) ? "destructive" : "outline"} className="gap-1">
              <CalendarIcon className="h-3 w-3" />
              {format(new Date(task.dueDate), 'PP', { locale: es })}
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-right space-x-2">
          <Button variant="ghost" size="icon" onClick={() => handleEdit(task)}>
            <PencilIcon className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(task.id)}>
            <TrashIcon className="w-4 h-4 text-destructive" />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Tareas</h1>
          <p className="text-muted-foreground mt-1">Gestiona tus tareas y proyectos pendientes.</p>
        </div>
        <Button onClick={handleNew}>
          <PlusIcon className="mr-2 h-4 w-4" /> Nueva Tarea
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={priorityFilter} onValueChange={(val) => setPriorityFilter(val as TaskPriority | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Prioridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {priorityOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as TaskFilter)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  {statusFilterOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} title="Limpiar filtros">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground mt-2">
              Mostrando {filteredTasks.length} de {tasks.length} tareas
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pending Tasks */}
      <Card>
        <CardHeader>
          <CardTitle>Tareas Pendientes ({pendingTasks.length})</CardTitle>
          <CardDescription>Tareas que aún no has completado.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Tarea</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Fecha Límite</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      <LoadingState message="Cargando tareas..." fullHeight={false} />
                    </TableCell>
                  </TableRow>
                ) : pendingTasks.length > 0 ? (
                  pendingTasks.map(task => <TaskRow key={task.id} task={task} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      {hasActiveFilters ? 'No hay tareas que coincidan con los filtros.' : 'No tienes tareas pendientes. ¡Buen trabajo!'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tareas Completadas ({completedTasks.length})</CardTitle>
            <CardDescription>Tareas que ya has terminado.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Tarea</TableHead>
                    <TableHead>Prioridad</TableHead>
                    <TableHead>Fecha Límite</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedTasks.map(task => <TaskRow key={task.id} task={task} />)}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{currentTask?.id ? 'Editar Tarea' : 'Crear Tarea'}</DialogTitle>
          </DialogHeader>
          {currentTask && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Título de la tarea *</label>
                <Input
                  placeholder="Ej: Llamar al cliente, Revisar contrato..."
                  value={currentTask.title || ''}
                  onChange={e => setCurrentTask({ ...currentTask, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Descripción (opcional)</label>
                <Textarea
                  placeholder="Añade más detalles sobre esta tarea..."
                  value={currentTask.descripcion || ''}
                  onChange={e => setCurrentTask({ ...currentTask, descripcion: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Prioridad</label>
                <Select
                  value={currentTask.priority || 'normal'}
                  onValueChange={(value: TaskPriority) => setCurrentTask({ ...currentTask, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona la prioridad" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="capitalize">{option.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Fecha límite (opcional)</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP', { locale: es }) : 'Selecciona una fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
                {selectedDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDate(undefined)}
                    className="w-full"
                  >
                    Limpiar fecha
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar Tarea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(isOpen) => !isOpen && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta tarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer y eliminará la tarea de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Confirmar y Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
